import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AdminSettings {
  site_name: string;
  support_email: string;
  site_description: string;
  two_factor_required: boolean;
  password_expiry_days: number;
  max_login_attempts: number;
  new_application_alerts: boolean;
  quote_request_notifications: boolean;
  system_health_alerts: boolean;
  backup_frequency: string;
  data_retention_days: number;
  auto_purge_enabled: boolean;
}

export const useAdminSettings = () => {
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('admin-settings', {
          body: { action: 'get' }
        });

        if (error) {
          throw error;
        }

        setSettings(data.data);
      } catch (error) {
        console.error('Error loading settings:', error);
        toast({
          title: 'Error',
          description: 'Failed to load settings',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [toast]);

  // Save settings
  const saveSettings = async (newSettings: AdminSettings) => {
    setIsSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-settings', {
        body: { action: 'save', settings: newSettings }
      });

      if (error) {
        throw error;
      }

      setSettings(newSettings);
      toast({
        title: 'Success',
        description: 'Settings saved successfully'
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Send test email
  const sendTestEmail = async () => {
    try {
      const { error } = await supabase.functions.invoke('send-admin-notification', {
        body: {
          type: 'test',
          data: {
            message: 'This is a test notification from the admin settings panel.'
          }
        }
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Test email sent successfully'
      });
    } catch (error) {
      console.error('Error sending test email:', error);
      toast({
        title: 'Info',
        description: 'Test email functionality would be implemented with email service',
        variant: 'default'
      });
    }
  };

  // Clear user sessions
  const clearAllUserSessions = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('admin-settings', {
        body: { action: 'clear_all_sessions' }
      });

      if (error) {
        throw error;
      }

      toast({
        title: 'Success',
        description: 'All user sessions cleared successfully'
      });
    } catch (error) {
      console.error('Error clearing sessions:', error);
      toast({
        title: 'Error',
        description: 'Failed to clear user sessions',
        variant: 'destructive'
      });
    }
  };

  return {
    settings,
    isLoading,
    isSaving,
    saveSettings,
    sendTestEmail,
    clearAllUserSessions
  };
};